import { FC } from 'react';
import { IonContent, IonItem, IonLabel, IonModal, IonToggle } from '@ionic/react';
import { useInstance } from '../support/ioc/useInstance';
import { useSubscription } from '@autopylot-internal/state';
import { createUseStyles } from 'react-jss';
import { mediaMinWidth } from '../support/helpers/breakpoints.ts';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { useBreakpoints } from '../support/hooks/useBreakpoints.tsx';
import { PageHeader } from '../components/page/PageHeader.tsx';
import { OperatorState } from './OperatorState.ts';
import { INotificationKey, INotificationType } from './IOperator.ts';

const useStyles = createUseStyles({
    section: {
        paddingBottom: 32,

        // 2-col layout on larger screens
        [mediaMinWidth.sm]: {
            width: '100%',
            display: 'flex',
            gap: 30
        }
    }
});

const LabeledToggle: FC<{label: string, value: boolean, onChange: (value: boolean) => void}> = ({ label, value, onChange }) => {
    return (
        <IonItem style={{cursor: 'pointer'}} lines="none" onClick={() => onChange(!value)}>
            <IonLabel style={{paddingRight: 10}}>{label}</IonLabel>
            <IonToggle checked={value} />
        </IonItem>
    );
};

export type INotificationSettingsProps = {
    isOpen?: boolean;
    onClose?: () => void;
};

export const NotificationSettingsModal: FC<INotificationSettingsProps> = ({ isOpen = false, onClose }) => {
    const classes = useStyles();

    const operatorState = useInstance(OperatorState);
    useSubscription(() => operatorState);

    const tracker = useInstance(TrackingService);

    const { isAboveBreakpoint } = useBreakpoints();

    const settings = operatorState.state.notificationSettings;

    const toggleLabels: {[type in INotificationType]: string} = {
        email: 'Email',
        push: isAboveBreakpoint('sm') ? 'Push' : 'Push Notification'
    };

    const loadSettings = async () => {
        tracker.track('Notification Settings Opened');
    };

    const settingChanged = async (key: INotificationKey, attr: INotificationType, value: 'enabled' | 'disabled') => {
        await operatorState.save({
            notificationSettings: {
                ...settings,
                [key]: {
                    ...settings?.[key],
                    [attr]: value
                }
            }
        });
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            onDidPresent={loadSettings}
        >
            <PageHeader title="Notification Settings" onClose={onClose} />

            <IonContent className="ion-padding">
                <div className={classes.section}>
                    <div>
                        <h4>LAANC Confirmation</h4>
                        <p style={{marginTop: 5}}>Get a confirmation email whenever you create or cancel a LAANC mission.</p>
                    </div>
                    <div style={{marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                            <LabeledToggle
                                label={toggleLabels.email}
                                value={settings?.laancConfirmation.email !== 'disabled'} // defaults to enabled
                                onChange={v => settingChanged('laancConfirmation', 'email', v ? 'enabled' : 'disabled')}
                            />
                        </div>
                    </div>
                </div>
            </IonContent>

        </IonModal>
    );
};
